import { CalendarPlus, CalendarClock, Clock, Lock, Users } from 'lucide-react'
import { format, max, min } from 'date-fns'
import { useMemo } from 'react'
import type { SurveyResponseItem } from '@bandos/shared'
import { ClosedBadge, MetaBadge, MetaRow } from './SurveyInsightsPageStyles'

export interface SurveyInsightsMetaProps {
  isClosed: boolean
  createdAt: string
  responses: SurveyResponseItem[]
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

/**
 * Meta-row badges for the survey insights page: closed pill, created date,
 * response count, response date range, and average completion time.
 */
export function SurveyInsightsMeta({ isClosed, createdAt, responses }: SurveyInsightsMetaProps) {
  const createdLabel = useMemo(() => format(new Date(createdAt), 'MMM d, yyyy'), [createdAt])

  const responsesMeta = useMemo(() => {
    if (responses.length === 0) return null

    const dates = responses.map((r) => new Date(r.submittedAt))
    const earliest = format(min(dates), 'MMM d, yyyy')
    const latest = format(max(dates), 'MMM d, yyyy')

    const validDurations = responses
      .map((r) => r.durationSeconds)
      .filter((d): d is number => typeof d === 'number' && d > 0)

    const avgDuration =
      validDurations.length > 0
        ? Math.round(validDurations.reduce((s, d) => s + d, 0) / validDurations.length)
        : null

    return { earliest, latest, avgDuration }
  }, [responses])

  return (
    <MetaRow>
      {isClosed && (
        <ClosedBadge>
          <Lock size={13} />
          Closed
        </ClosedBadge>
      )}
      <MetaBadge title="When this survey was created">
        <CalendarPlus size={13} />
        Created {createdLabel}
      </MetaBadge>
      <MetaBadge>
        <Users size={13} />
        {responses.length} response{responses.length !== 1 ? 's' : ''}
      </MetaBadge>
      {responsesMeta && (
        <MetaBadge title="When responses were submitted">
          <CalendarClock size={13} />
          Responses{' '}
          {responsesMeta.earliest === responsesMeta.latest
            ? responsesMeta.earliest
            : `${responsesMeta.earliest} – ${responsesMeta.latest}`}
        </MetaBadge>
      )}
      {responsesMeta?.avgDuration != null && (
        <MetaBadge>
          <Clock size={13} />
          {formatDuration(responsesMeta.avgDuration)} avg completion
        </MetaBadge>
      )}
    </MetaRow>
  )
}
